/*
 * Gmarket's buy box.
 *
 * Shopify renders the product page; this answers the one thing Shopify cannot
 * know — who near you has this, at what price — and puts the result in the
 * cart that actually takes the money (the Gmarket app, not Shopify).
 *
 * ---------------------------------------------------------------------------
 * THREE STATES, AND THEY ARE NOT A FALLBACK CHAIN
 * ---------------------------------------------------------------------------
 * Per variant, from decision 21:
 *
 *   1. dealers hold it        -> list them, nearest first, each with a price
 *   2. only the wholesaler    -> "have it sent to a dealer", pick who receives
 *   3. nobody                 -> out of stock, offer to email when it returns
 *
 * State 2 is a real sale, not a consolation. Most of the catalogue sits there,
 * which is why it gets a dealer picker rather than a line of apology.
 *
 * ---------------------------------------------------------------------------
 * NO FRAMEWORK, NO BUILD STEP
 * ---------------------------------------------------------------------------
 * Plain DOM in a Shopify asset. This has to live inside a legacy Bootstrap
 * theme that uses {% include %} and jQuery, and a bundler in that context buys
 * nothing but a deployment story. Everything is created with createElement
 * rather than innerHTML: dealer names, suburbs and lead-time messages are
 * operator-supplied strings, and the one place markup could be injected into a
 * storefront is the one place not to concatenate HTML.
 */
(function () {
  "use strict";

  var POSTCODE_KEY = "gmarket_postcode";
  var SORT_KEY = "gmarket_seller_sort";

  /**
   * The sort options, in the order they are offered.
   *
   * Values match the API's own, because the SORT HAPPENS SERVER-SIDE — the
   * response holds at most 25 dealers, so reordering here would reorder the
   * nearest 25 and call the 25th-nearest "furthest". Changing the sort is
   * therefore a refetch, not a re-render.
   *
   * `needsPostcode` hides the two distance options until there is a postcode
   * to measure from. Offering "Nearest first" with nothing to sort on would
   * either lie or silently fall back, and the API does fall back — to price —
   * so the list would simply not be in the order the dropdown claimed.
   */
  var SORTS = [
    { value: "nearest", label: "Closest first", needsPostcode: true },
    { value: "furthest", label: "Furthest first", needsPostcode: true },
    { value: "price-asc", label: "Cheapest first", needsPostcode: false },
    { value: "price-desc", label: "Dearest first", needsPostcode: false },
  ];
  var CART_TOKEN_KEY = "gmarket_cart_token";
  var CART_TOKEN_HEADER = "x-gmarket-cart";


  /**
   * localStorage, wrapped.
   *
   * Private windows, blocked site data and a few embedded browsers make these
   * throw rather than return null, which would take the whole buy box down over
   * a remembered postcode. Every failure degrades to "we don't know your
   * postcode", which is a supported state anyway.
   */
  function remembered(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function remember(key, value) {
    try {
      if (value === null) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, value);
    } catch (e) {
      /* no-op: see remembered() */
    }
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  }

  function money(cents) {
    if (cents === null || cents === undefined) return "";
    return (cents / 100).toLocaleString("en-AU", {
      style: "currency",
      currency: "AUD",
    });
  }

  /** "12 km away", or nothing when we have no postcode to measure from. */
  function distance(km) {
    if (km === null || km === undefined) return "";
    if (km < 1) return "under 1 km away";
    return Math.round(km) + " km away";
  }

  function place(suburb, state) {
    return [suburb, state].filter(Boolean).join(" ");
  }

  /**
   * Where a dealer's own shopfront lives.
   *
   * Shopify cannot route /dealers/<slug> to a template without an app proxy,
   * so a dealer page is a page template reading ?slug= — see
   * templates/page.gmarket-dealer.liquid. If the page is created under a
   * different handle than "dealers", this is the one place to change.
   */
  var DEALER_PAGE = "/pages/dealers";

  function dealerUrl(slug) {
    return DEALER_PAGE + "?slug=" + encodeURIComponent(slug);
  }

  function BuyBox(root) {
    this.root = root;
    this.api = root.getAttribute("data-api");
    this.handle = root.getAttribute("data-handle");
    this.productTitle = root.getAttribute("data-product-title") || "";
    this.variants = this.readVariants();
    this.postcode = remembered(POSTCODE_KEY);
    /** The open fulfilment panel, if any. One at a time — see openFulfilment. */
    this.panel = null;
    // Remembered across products: somebody who sorts by price once is usually
    // shopping on price. Coerced to a valid value, since localStorage is
    // writable by anything on the origin and an unknown sort is a 400.
    var savedSort = remembered(SORT_KEY);
    this.sort = SORTS.some(function (s) {
      return s.value === savedSort;
    })
      ? savedSort
      : "nearest";
    this.data = null;
    this.selectedSku = this.initialSku();
  }

  BuyBox.prototype.readVariants = function () {
    var node = document.querySelector("[data-gmarket-variants]");
    if (!node) return [];
    try {
      return JSON.parse(node.textContent) || [];
    } catch (e) {
      return [];
    }
  };

  /**
   * Which variant is showing.
   *
   * Read from the URL first, because Shopify puts ?variant= there and that is
   * what a shared or bookmarked link carries — honouring the select element
   * alone would show the first variant to someone who followed a link to the
   * third.
   */
  BuyBox.prototype.initialSku = function () {
    var params = new URLSearchParams(window.location.search);
    var id = params.get("variant");
    if (id) {
      var match = this.variants.filter(function (v) {
        return String(v.id) === String(id);
      })[0];
      if (match) return match.sku;
    }
    return this.variants.length ? this.variants[0].sku : null;
  };

  BuyBox.prototype.load = function () {
    var self = this;

    // A blank host is its own failure, and it used to be indistinguishable
    // from a broken app. If snippets/gmarket-config.liquid is missing or its
    // assign has not leaked, data-api is empty, the fetch below becomes a
    // RELATIVE url, the storefront answers its own 404, and the customer is
    // told dealer stock could not be loaded. That cost a whole upload cycle
    // once; it now names the actual problem.
    if (!this.api) {
      this.root.textContent = "";
      this.root.appendChild(
        this.notice(
          "Dealer stock isn't configured for this theme " +
            "(snippets/gmarket-config.liquid is missing or empty).",
          "gmarket-note--error",
        ),
      );
      if (window.console) {
        console.error(
          "[gmarket] data-api is empty — is snippets/gmarket-config.liquid " +
            "uploaded, and included rather than rendered?",
        );
      }
      return;
    }

    var url =
      this.api +
      "/api/public/product-sellers?handle=" +
      encodeURIComponent(this.handle) +
      (this.postcode ? "&postcode=" + encodeURIComponent(this.postcode) : "") +
      "&sort=" +
      encodeURIComponent(this.sort);

    return fetch(url, { headers: { Accept: "application/json" } })
      .then(function (response) {
        if (!response.ok) throw new Error("HTTP " + response.status);
        return response.json();
      })
      .then(function (data) {
        self.data = data;
        self.render();
      })
      .catch(function (error) {
        // Says what it cannot do rather than vanishing. A buy box that renders
        // nothing reads as "this product is unavailable", which is a claim we
        // have no basis for when the request simply failed.
        self.renderError();
        if (window.console) console.error("[gmarket] buy box", error);
      });
  };

  BuyBox.prototype.variantData = function () {
    if (!this.data || !this.data.variants) return null;
    var sku = this.selectedSku;
    var matches = this.data.variants.filter(function (v) {
      return v.sku && sku && v.sku === sku;
    });
    return matches.length ? matches[0] : null;
  };

  BuyBox.prototype.render = function () {
    var root = this.root;
    root.textContent = "";
    // Wiping the root detaches any open fulfilment panel, so the reference to
    // it has to go too. Without this, a variant change left a stale panel on
    // record and the next click on that same dealer was read as "close the one
    // that's already open" — so nothing happened at all.
    this.panel = null;

    root.appendChild(this.renderPostcode());

    if (!this.data || this.data.found === false) {
      root.appendChild(
        this.notice(
          "We don't have dealer stock for this product yet. Ask us and we'll " +
            "find out who can get it.",
        ),
      );
      return;
    }

    var variant = this.variantData();
    if (!variant) {
      // A Shopify variant with no SKU, or one the catalogue has not seen. Said
      // plainly: the alternative is an empty box that looks like a bug, and a
      // missing SKU is a real catalogue fault somebody should fix.
      root.appendChild(
        this.notice(
          "We can't match this option to our dealer catalogue. Get in touch " +
            "and we'll sort it out.",
        ),
      );
      return;
    }

    if (variant.sellers && variant.sellers.length > 0) {
      root.appendChild(this.renderDealers(variant));
    } else if (variant.supplier && variant.supplier.available) {
      root.appendChild(this.renderSendToDealer(variant));
    } else {
      root.appendChild(this.renderOutOfStock(variant));
    }

    if (this.data.requiresTransfer) {
      root.appendChild(
        this.notice(
          "This is handed over in person by the dealer. They'll confirm what " +
            "you need to bring.",
          "gmarket-note--transfer",
        ),
      );
    }
  };

  // --- postcode -----------------------------------------------------------

  BuyBox.prototype.renderPostcode = function () {
    var self = this;
    var wrap = el("div", "gmarket-postcode");

    var form = el("form", "gmarket-postcode__form");
    var label = el("label", "gmarket-postcode__label");
    label.setAttribute("for", "gmarket-postcode-input");
    label.textContent = this.postcode
      ? "Showing dealers near"
      : "Your postcode";

    var input = el("input", "gmarket-postcode__input");
    input.type = "text";
    input.id = "gmarket-postcode-input";
    input.inputMode = "numeric";
    input.maxLength = 4;
    input.placeholder = "2000";
    input.value = this.postcode || "";

    var button = el("button", "gmarket-postcode__button", this.postcode ? "Change" : "Find dealers");
    button.type = "submit";

    form.appendChild(label);
    form.appendChild(input);
    form.appendChild(button);
    wrap.appendChild(form);

    form.addEventListener("submit", function (event) {
      event.preventDefault();
      var value = input.value.trim();
      // Four digits or nothing. The API rejects anything else rather than
      // silently returning an unsorted list, so checking here saves a round
      // trip and gives a better message than the API's.
      if (value && !/^\d{4}$/.test(value)) {
        wrap.appendChild(
          self.notice("An Australian postcode is four digits.", "gmarket-note--error"),
        );
        return;
      }
      self.postcode = value || null;
      remember(POSTCODE_KEY, self.postcode);
      self.setLoading("Finding dealers near " + (value || "you") + "…");
      self.load();
    });

    if (this.postcode && this.data && this.data.origin && !this.data.origin.located) {
      wrap.appendChild(
        self.notice(
          "We couldn't place " +
            this.postcode +
            ", so this list isn't sorted by distance.",
          "gmarket-note--error",
        ),
      );
    }

    return wrap;
  };

  // --- state 1: dealers have it -------------------------------------------

  BuyBox.prototype.renderDealers = function (variant) {
    var self = this;
    var wrap = el("div", "gmarket-dealers");

    var heading = el(
      "h3",
      "gmarket-dealers__heading",
      variant.sellerCount === 1
        ? "1 dealer has this in stock"
        : variant.sellerCount + " dealers have this in stock",
    );
    wrap.appendChild(heading);

    if (!this.postcode) {
      wrap.appendChild(
        this.notice("Enter your postcode above to see which is closest."),
      );
    }

    // Only once there is something to reorder. A sort control above a single
    // dealer is furniture that asks the customer to make a decision with no
    // consequence.
    if (variant.sellers.length > 1) wrap.appendChild(this.renderSort());

    var list = el("ul", "gmarket-dealers__list");
    variant.sellers.forEach(function (seller) {
      list.appendChild(self.dealerRow(variant, seller));
    });
    wrap.appendChild(list);

    // The response caps the list; sellerCount is the true total. Saying so
    // stops "why is my dealer not here" being a mystery.
    if (variant.sellerCount > variant.sellers.length) {
      wrap.appendChild(
        this.notice(
          "Showing the " +
            variant.sellers.length +
            " closest of " +
            variant.sellerCount +
            ".",
        ),
      );
    }

    return wrap;
  };

  /**
   * The sort control.
   *
   * Reads the APPLIED sort from the response, not the requested one. The API
   * falls back to price when a distance sort has no postcode to measure from,
   * and a dropdown reading "Closest first" above a price-ordered list is worse
   * than one that has visibly moved.
   */
  BuyBox.prototype.renderSort = function () {
    var self = this;
    var applied = (this.data && this.data.sort) || this.sort;

    var wrap = el("div", "gmarket-sort");
    var label = el("label", "gmarket-sort__label", "Sort by");
    label.setAttribute("for", "gmarket-sort-select");

    var select = el("select", "gmarket-sort__select");
    select.id = "gmarket-sort-select";

    SORTS.forEach(function (option) {
      if (option.needsPostcode && !self.postcode) return;
      var node = document.createElement("option");
      node.value = option.value;
      node.textContent = option.label;
      if (option.value === applied) node.selected = true;
      select.appendChild(node);
    });

    select.addEventListener("change", function () {
      self.sort = select.value;
      remember(SORT_KEY, self.sort);
      // A refetch, not a re-render — see SORTS for why.
      self.setLoading("Reordering…");
      self.load();
    });

    wrap.appendChild(label);
    wrap.appendChild(select);
    return wrap;
  };

  BuyBox.prototype.dealerRow = function (variant, seller) {
    var self = this;
    var row = el("li", "gmarket-dealer");

    var details = el("div", "gmarket-dealer__details");

    // The dealer's name links to their shopfront. A customer who likes the look
    // of a particular dealer — closest, cheapest, one they have dealt with —
    // should be able to see what else they have without going back to a
    // collection page and starting again.
    var name = el("a", "gmarket-dealer__name", seller.businessName);
    name.href = dealerUrl(seller.slug);
    details.appendChild(name);

    var where = place(seller.suburb, seller.state);
    var far = distance(seller.distanceKm);
    if (where || far) {
      details.appendChild(
        el(
          "span",
          "gmarket-dealer__where",
          [where, far].filter(Boolean).join(" · "),
        ),
      );
    }

    var action = el("div", "gmarket-dealer__action");
    action.appendChild(el("span", "gmarket-dealer__price", money(seller.priceCents)));

    var button = el("button", "gmarket-dealer__add", "Add to cart");
    button.type = "button";
    button.addEventListener("click", function () {
      // A FIREARM GETS THE CHOICE; EVERYTHING ELSE DOES NOT, YET.
      //
      // Collection or transfer-to-a-dealer changes the price, so a firearm
      // cannot be added without asking. For anything else the only destination
      // currently implemented is the dealer's own counter — posting to the
      // customer's own address needs the destination to come from their
      // postcode, which lands with cart totals — so opening a panel offering
      // one option would be a click that decides nothing.
      if (!self.data || !self.data.requiresTransfer) {
        self.addLine(button, {
          kind: "dealer",
          catalogueVariantId: variant.catalogueVariantId,
          sellerSlug: seller.slug,
          quantity: 1,
        });
        return;
      }
      self.openFulfilment(row, variant, seller);
    });
    action.appendChild(button);

    row.appendChild(details);
    row.appendChild(action);
    return row;
  };

  /**
   * Opens the fulfilment panel under one dealer's row, closing any other.
   *
   * One at a time on purpose: two open panels mean two totals on screen and no
   * way to tell which one the next click commits to.
   */
  BuyBox.prototype.openFulfilment = function (row, variant, seller) {
    var self = this;

    if (!window.GmarketFulfilment) {
      // Only reachable if assets/gmarket-fulfilment.js did not load. Said
      // rather than silently falling back to adding it as collection, which
      // would answer the customer's question for them with the cheaper option
      // and put a firearm in their cart on terms they never chose.
      this.root.appendChild(
        this.notice(
          "Couldn't load the delivery options. Refresh and try again.",
          "gmarket-note--error",
        ),
      );
      return;
    }

    if (this.panel) {
      var same = this.panel.seller.slug === seller.slug;
      this.panel.close();
      // Clicking the same dealer's button again closes it, which is what a
      // customer who opened it by mistake will try.
      if (same) return;
    }

    this.panel = window.GmarketFulfilment.open(
      {
        api: this.api,
        confirmLabel: "Add to cart",
        notice: function (text, className) {
          return self.notice(text, className);
        },
        commit: function (fulfilment, button, done) {
          self.addLine(
            button,
            {
              kind: "dealer",
              catalogueVariantId: variant.catalogueVariantId,
              sellerSlug: seller.slug,
              quantity: 1,
              fulfilment: fulfilment,
            },
            done,
          );
        },
        onClose: function () {
          self.panel = null;
        },
      },
      {
        container: row,
        catalogueVariantId: variant.catalogueVariantId,
        sellerSlug: seller.slug,
      },
    );
  };

  // --- state 2: the wholesaler has it ------------------------------------

  BuyBox.prototype.renderSendToDealer = function (variant) {
    var self = this;
    var supplier = variant.supplier;
    var wrap = el("div", "gmarket-supplier");

    wrap.appendChild(
      el("h3", "gmarket-supplier__heading", "No dealer has this on the shelf"),
    );
    wrap.appendChild(
      el(
        "p",
        "gmarket-supplier__lead",
        "It's available from the wholesaler. Choose a dealer to order it in and " +
          "hand it over to you.",
      ),
    );

    if (supplier.priceCents !== null && supplier.priceCents !== undefined) {
      wrap.appendChild(
        el("p", "gmarket-supplier__price", money(supplier.priceCents)),
      );
    }

    if (supplier.leadTimeMessage) {
      wrap.appendChild(
        this.notice(supplier.leadTimeMessage, "gmarket-note--lead-time"),
      );
    }

    if (!this.postcode) {
      wrap.appendChild(
        this.notice("Enter your postcode above to see which dealers are closest."),
      );
    }

    var select = el("select", "gmarket-supplier__select");
    select.setAttribute("aria-label", "Dealer to receive your order");
    supplier.dealers.forEach(function (dealer) {
      var option = document.createElement("option");
      option.value = dealer.slug;
      var where = place(dealer.suburb, dealer.state);
      var far = distance(dealer.distanceKm);
      option.textContent =
        dealer.businessName +
        (where ? " — " + where : "") +
        (far ? " (" + far + ")" : "");
      select.appendChild(option);
    });
    wrap.appendChild(select);

    var button = el("button", "gmarket-supplier__add", "Send to this dealer");
    button.type = "button";
    button.addEventListener("click", function () {
      self.addLine(button, {
        kind: "supplier",
        catalogueVariantId: variant.catalogueVariantId,
        sellerSlug: select.value,
        quantity: 1,
      });
    });
    wrap.appendChild(button);

    if (supplier.dealerCount > supplier.dealers.length) {
      wrap.appendChild(
        this.notice(
          "Showing the " +
            supplier.dealers.length +
            " closest of " +
            supplier.dealerCount +
            " who can order this in.",
        ),
      );
    }

    return wrap;
  };

  // --- state 3: nobody has it --------------------------------------------

  BuyBox.prototype.renderOutOfStock = function (variant) {
    var self = this;
    var wrap = el("div", "gmarket-oos");

    wrap.appendChild(el("h3", "gmarket-oos__heading", "Out of stock"));
    wrap.appendChild(
      el(
        "p",
        "gmarket-oos__lead",
        "No dealer has this and the wholesaler is out too. We'll email you once " +
          "it's back — once, not every time.",
      ),
    );

    var form = el("form", "gmarket-oos__form");
    var input = el("input", "gmarket-oos__input");
    input.type = "email";
    input.required = true;
    input.placeholder = "you@example.com";
    input.setAttribute("aria-label", "Email address");

    var button = el("button", "gmarket-oos__button", "Email me when it's back");
    button.type = "submit";

    form.appendChild(input);
    form.appendChild(button);
    wrap.appendChild(form);

    form.addEventListener("submit", function (event) {
      event.preventDefault();
      button.disabled = true;
      button.textContent = "Saving…";

      fetch(self.api + "/api/public/back-in-stock", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          catalogueVariantId: variant.catalogueVariantId,
          email: input.value.trim(),
        }),
      })
        .then(function (response) {
          return response.json().then(function (data) {
            return { ok: response.ok, data: data };
          });
        })
        .then(function (result) {
          wrap.textContent = "";
          wrap.appendChild(el("h3", "gmarket-oos__heading", "Out of stock"));
          wrap.appendChild(
            el(
              "p",
              result.ok ? "gmarket-oos__done" : "gmarket-oos__error",
              (result.data && (result.data.message || result.data.error)) ||
                "We'll be in touch.",
            ),
          );
        })
        .catch(function () {
          button.disabled = false;
          button.textContent = "Email me when it's back";
          wrap.appendChild(
            self.notice("That didn't save. Try again?", "gmarket-note--error"),
          );
        });
    });

    return wrap;
  };

  // --- adding to the cart -------------------------------------------------

  /**
   * Sends one line to the Gmarket cart.
   *
   * `credentials: "include"` AND the token header, both. The cart cookie is a
   * third-party cookie from the storefront's point of view, and Safari blocks
   * those outright — a cookie-only cart would present as an empty basket on
   * every iPhone, which reads as a bug in the cart rather than a cookie policy.
   * The header is what actually carries the cart there; the cookie is worth
   * keeping because it survives cleared localStorage.
   */
  /**
   * `done` is called with true on success and false on refusal, so a caller
   * that put the button inside its own UI can decide whether to close. The
   * fulfilment panel stays open on refusal: a dealer who stopped posting to
   * that state while the panel was open should leave the customer looking at
   * the choices with the reason above them, not back at the start.
   */
  BuyBox.prototype.addLine = function (button, line, done) {
    var self = this;
    // An error about an add made from inside the fulfilment panel belongs in
    // the panel, not at the foot of the whole buy box where it can sit below
    // the thing the customer is reading.
    var errorTarget = (this.panel && this.panel.node) || this.root;
    var original = button.textContent;
    button.disabled = true;
    button.textContent = "Adding…";

    var headers = { "Content-Type": "application/json" };
    var token = remembered(CART_TOKEN_KEY);
    if (token) headers[CART_TOKEN_HEADER] = token;

    fetch(this.api + "/api/public/cart", {
      method: "POST",
      credentials: "include",
      headers: headers,
      body: JSON.stringify(line),
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        button.disabled = false;
        button.textContent = original;

        if (!result.ok) {
          errorTarget.appendChild(
            self.notice(
              (result.data && result.data.error) || "That couldn't be added.",
              "gmarket-note--error",
            ),
          );
          if (done) done(false);
          return;
        }

        if (result.data.token) remember(CART_TOKEN_KEY, result.data.token);
        self.showAdded(result.data);
        if (done) done(true);
      })
      .catch(function () {
        button.disabled = false;
        button.textContent = original;
        errorTarget.appendChild(
          self.notice("Couldn't reach the cart. Try again?", "gmarket-note--error"),
        );
        if (done) done(false);
      });
  };

  BuyBox.prototype.showAdded = function (cart) {
    var existing = this.root.querySelector(".gmarket-added");
    if (existing) existing.remove();

    var box = el("div", "gmarket-added");
    box.appendChild(
      el(
        "span",
        "gmarket-added__text",
        "In your cart: " +
          cart.itemCount +
          (cart.itemCount === 1 ? " item" : " items") +
          " · " +
          money(cart.subtotalCents),
      ),
    );

    // The STOREFRONT's /cart, not the app's. The cart token is in
    // localStorage on this origin and localStorage is per-origin, so a cart
    // page on the app's domain could not read it — it would fall back to the
    // third-party cookie that Safari blocks, and show an empty cart.
    var link = el("a", "gmarket-added__link", "Go to cart");
    link.href = "/cart";
    box.appendChild(link);

    this.root.appendChild(box);
  };

  // --- plumbing -----------------------------------------------------------

  BuyBox.prototype.notice = function (text, className) {
    return el("p", "gmarket-note " + (className || ""), text);
  };

  BuyBox.prototype.setLoading = function (text) {
    this.root.textContent = "";
    var box = el("div", "gmarket-buy-box__loading");
    box.appendChild(el("span", "gmarket-spinner"));
    box.appendChild(el("span", null, text));
    this.root.appendChild(box);
  };

  BuyBox.prototype.renderError = function () {
    this.root.textContent = "";
    this.root.appendChild(this.renderPostcode());
    this.root.appendChild(
      this.notice(
        "We couldn't load dealer stock just now. Refresh, or get in touch and " +
          "we'll check for you.",
        "gmarket-note--error",
      ),
    );
  };

  /**
   * Re-reads the selected variant when the theme changes it.
   *
   * This theme drives variants through a hidden <select name="id"> and its own
   * jQuery, with no custom event to listen for — so the selection is watched
   * rather than subscribed to. A `change` listener on that select covers the
   * theme's own swatches and dropdowns; popstate covers the back button, since
   * the theme pushes ?variant= into history.
   */
  BuyBox.prototype.watchVariant = function () {
    var self = this;

    function syncFromId(id) {
      var match = self.variants.filter(function (v) {
        return String(v.id) === String(id);
      })[0];
      if (!match || match.sku === self.selectedSku) return;
      self.selectedSku = match.sku;
      self.render();
    }

    document.querySelectorAll('select[name="id"]').forEach(function (select) {
      select.addEventListener("change", function () {
        syncFromId(select.value);
      });

      // The product form still renders, because the theme's variant selectors
      // and its own product JS both live in it — only the submit and payment
      // buttons were removed. With no submit button there is normally no way to
      // submit it, but Enter in a form can still do so in some browsers, and
      // that would post straight to Shopify's cart. Belt and braces.
      var form = select.form;
      if (form && !form.dataset.gmarketGuarded) {
        form.dataset.gmarketGuarded = "1";
        form.addEventListener("submit", function (event) {
          event.preventDefault();
        });
      }
    });

    window.addEventListener("popstate", function () {
      var id = new URLSearchParams(window.location.search).get("variant");
      if (id) syncFromId(id);
    });
  };

  function start() {
    var roots = document.querySelectorAll("[data-gmarket-buy-box]");
    for (var i = 0; i < roots.length; i += 1) {
      var box = new BuyBox(roots[i]);
      box.watchVariant();
      box.load();
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
